import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/** Token de cor de um tipo (mapeado para classes em corClasses). */
export type CorTipo = "danger" | "warning" | "success" | "info" | "primary";

export interface TipoPropostaRow {
  categoria: string;        // chave = categoria em planos_proposta_catalogo (ex.: 'tt_pos')
  nome: string;             // nome exibido no botão da proposta E na seção de Config
  cor: CorTipo | string;
  icone: string | null;
  ordem: number;
  kind: string;             // comportamento: tt|tt_pre|portabilidade|renov|nova|bloco|fibra|tv|servico
  produto_pedido_id: string | null;
  ativo: boolean;
}

export const TIPOS_PROPOSTA_QUERY_KEY = ["proposta-tipos"] as const;

/** token de cor → classes Tailwind (mesmo DNA do montador). */
export function corClassesTipo(cor: string): { bg: string; bgSolid: string; text: string; border: string } {
  switch (cor) {
    case "danger":  return { bg: "bg-danger/10",  bgSolid: "bg-danger",  text: "text-danger",  border: "border-danger" };
    case "warning": return { bg: "bg-warning/10", bgSolid: "bg-warning", text: "text-warning", border: "border-warning" };
    case "success": return { bg: "bg-success/10", bgSolid: "bg-success", text: "text-success", border: "border-success" };
    case "info":    return { bg: "bg-info/10",    bgSolid: "bg-info",    text: "text-info",    border: "border-info" };
    case "primary":
    default:        return { bg: "bg-primary/[0.10]", bgSolid: "bg-primary", text: "text-primary dark:text-foreground", border: "border-primary/30" };
  }
}

export const CORES_TIPO: { value: CorTipo; label: string }[] = [
  { value: "danger", label: "Vermelho" },
  { value: "warning", label: "Âmbar" },
  { value: "success", label: "Verde" },
  { value: "info", label: "Azul" },
  { value: "primary", label: "Bordô" },
];

/**
 * Registro único dos tipos da proposta (tabela proposta_tipos).
 * Fonte de nome/cor/ícone/ordem/comportamento/produto default — lido pelo montador
 * e por Planos & Preços. Editável/criável pela UO (admin).
 */
export function useTiposProposta() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: TIPOS_PROPOSTA_QUERY_KEY,
    queryFn: async (): Promise<TipoPropostaRow[]> => {
      const { data, error } = await supabase
        .from("proposta_tipos" as any)
        .select("*")
        .order("ordem");
      if (error) throw error;
      return ((data ?? []) as any[]).map((r) => ({
        ...r,
        ordem: Number(r.ordem) || 0,
        ativo: r.ativo !== false,
      })) as TipoPropostaRow[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: TIPOS_PROPOSTA_QUERY_KEY });

  const updateTipo = useMutation({
    mutationFn: async (input: {
      categoria: string;
      patch: Partial<Pick<TipoPropostaRow, "nome" | "cor" | "icone" | "ordem" | "ativo" | "produto_pedido_id">>;
    }) => {
      const { error } = await supabase
        .from("proposta_tipos" as any)
        .update({ ...input.patch, updated_at: new Date().toISOString() } as any)
        .eq("categoria", input.categoria);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: "Tipo atualizado" }); },
    onError: (e: any) => toast({ title: "Erro ao atualizar tipo", description: e?.message ?? String(e), variant: "destructive" }),
  });

  const createTipo = useMutation({
    mutationFn: async (input: {
      categoria: string; nome: string; cor?: string; icone?: string | null;
      ordem?: number; kind?: string; produto_pedido_id?: string | null;
    }) => {
      const payload = {
        categoria: input.categoria,
        nome: input.nome.trim(),
        cor: input.cor ?? "primary",
        icone: input.icone ?? "Sparkles",
        ordem: input.ordem ?? 500,
        kind: input.kind ?? "servico",
        produto_pedido_id: input.produto_pedido_id ?? null,
        ativo: true,
      };
      const { error } = await supabase.from("proposta_tipos" as any).insert([payload as any]);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); },
    onError: (e: any) => {
      const dup = e?.code === "23505" || /duplicate/i.test(e?.message ?? "");
      toast({ title: dup ? "Tipo já existe" : "Erro ao criar tipo", description: dup ? "Já existe um tipo com essa chave." : (e?.message ?? String(e)), variant: "destructive" });
    },
  });

  const byCategoria = useMemo(() => {
    const m = new Map<string, TipoPropostaRow>();
    for (const t of query.data ?? []) m.set(t.categoria, t);
    return m;
  }, [query.data]);

  return {
    ...query,
    tipos: query.data ?? [],
    byCategoria,
    updateTipo,
    createTipo,
  };
}
